import React, { useState, useEffect, useRef, useCallback } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft, Menu, Send, X, Trash2, MessageCircle, Plus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

export default function TufyChatPage() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showChatList, setShowChatList] = useState(false);
  const [chatList, setChatList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:8000";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChats = async () => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.get(`${API_BASE_URL}/children/tufy-chats/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { child_id: childId },
      });
      setChatList(response.data.results || response.data);
    } catch (error) {
      console.error("Error loading chats:", error);
    }
  };

  const loadChatMessages = useCallback(async (chatId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const response = await axios.get(
        `${API_BASE_URL}/children/tufy-chats/${chatId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages || []);
      setShowChatList(false);
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  const createNewChat = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const response = await axios.post(
        `${API_BASE_URL}/children/tufy-chats/create-chat/`,
        { child_id: childId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newChat = response.data;
      setChatList((prev) => [newChat, ...prev]);
      setCurrentChatId(newChat.id);
      setMessages([]);
      setShowChatList(false);
    } catch (error) {
      console.error("Error creating new chat:", error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, childId]);

  const handleDeleteChat = useCallback(async (chatIdToDelete, event) => {
    event.stopPropagation();
    if (window.confirm("Are you sure you want to delete this chat?")) {
      try {
        const token = localStorage.getItem("access");
        await axios.delete(`${API_BASE_URL}/children/tufy-chats/${chatIdToDelete}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const updatedChatList = chatList.filter(chat => chat.id !== chatIdToDelete);
        setChatList(updatedChatList);

        if (currentChatId === chatIdToDelete) {
          if (updatedChatList.length > 0) {
            const newCurrentChat = updatedChatList[0];
            setCurrentChatId(newCurrentChat.id);
            loadChatMessages(newCurrentChat.id);
          } else {
            createNewChat();
          }
        }
      } catch (error) {
        console.error("Error deleting chat:", error);
        alert("Failed to delete chat. Please try again.");
      }
    }
  }, [API_BASE_URL, chatList, currentChatId, createNewChat, loadChatMessages]);

  const initializeChat = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    try {
        const token = localStorage.getItem("access");
        const response = await axios.get(`${API_BASE_URL}/children/tufy-chats/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { child_id: childId },
        });
        const chats = response.data.results || response.data;
        setChatList(chats);

        // Check if the most recent chat is empty (has no last_message)
        // If so, reuse it to avoid spamming empty chats.
        // Otherwise, ensure we start fresh with a new chat as requested.
        const latestChat = chats.length > 0 ? chats[0] : null;
        
        if (latestChat && !latestChat.last_message) {
          setCurrentChatId(latestChat.id);
          await loadChatMessages(latestChat.id);
        } else {
          await createNewChat();
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        // Fallback: try to create a new chat anyway
        try {
          await createNewChat();
        } catch (retryError) {
          console.error("Failed to create new chat as fallback:", retryError);
          setLoading(false);
        }
      }
  }, [childId, API_BASE_URL, loadChatMessages, createNewChat]);

  useEffect(() => {
    if (childId) {
      initializeChat();
    }
  }, [childId, initializeChat]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;

    let activeChatId = currentChatId;

    // If no active chat, try to create one automatically
    if (!activeChatId) {
      try {
        setSending(true); // Prevent multiple clicks
        const token = localStorage.getItem("access");
        const response = await axios.post(
          `${API_BASE_URL}/children/tufy-chats/create-chat/`,
          { child_id: childId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newChat = response.data;
        setChatList((prev) => [newChat, ...prev]);
        setCurrentChatId(newChat.id);
        activeChatId = newChat.id;
        // Note: We don't setSending(false) here because we want to continue sending the message
      } catch (error) {
        console.error("Error creating chat for message:", error);
        alert("Could not create a chat session. Please refresh the page.");
        setSending(false);
        return;
      }
    }

    const userMessage = message.trim();
    setMessage("");
    setSending(true);

    const tempUserMsg = {
      id: Date.now(),
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const token = localStorage.getItem("access");
      const response = await axios.post(
        `${API_BASE_URL}/children/tufy-chats/${activeChatId}/send-message/`,
        { content: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(response.data.messages);
      loadChats();
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
      alert("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-4 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(`/question-answer/${childId}`)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-pink-50 rounded-full flex items-center justify-center shadow-md border-2 border-pink-200">
                <img src="/tufy.png" alt="Tufy" className="w-7 h-7 object-contain" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-[#1e3a5f]">Tufy AI</h1>
                <p className="text-xs text-gray-500">Child Development Assistant</p>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setShowChatList(!showChatList)} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Chat List Sidebar */}
        {showChatList && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 transition-all" onClick={() => setShowChatList(false)}>
            <div
              className="absolute right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl transform transition-transform"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-pink-500 to-rose-500">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-white">Chat History</h2>
                  <button 
                    onClick={() => setShowChatList(false)}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <button
                  onClick={createNewChat}
                  className="w-full bg-white/20 hover:bg-white/30 text-white py-2.5 px-4 rounded-lg transition-all flex items-center justify-center space-x-2 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span>New Chat</span>
                </button>
              </div>
              
              <div className="overflow-y-auto h-[calc(100%-140px)]">
                {chatList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                    <MessageCircle className="w-16 h-16 text-gray-300 mb-3" />
                    <p className="text-gray-500">No chats yet</p>
                  </div>
                ) : (
                  chatList.map((chat) => (
                    <div
                      key={chat.id}
                      onClick={() => {
                        setCurrentChatId(chat.id);
                        loadChatMessages(chat.id);
                      }}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-all hover:bg-gray-50 ${
                        currentChatId === chat.id ? "bg-pink-50 border-l-4 border-l-pink-500" : ""
                      }`}
                    >
                      <div className="flex justify-between items-start w-full">
                        <div className="flex-1 min-w-0 pr-2">
                          <p className="font-medium text-gray-800 text-sm truncate">
                            {chat.title || `Chat - ${formatDate(chat.created_at)}`}
                          </p>
                          {chat.last_message && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {chat.last_message.content}
                            </p>
                          )}
                          <span className="text-xs text-gray-400 mt-1 block">
                            {formatTime(chat.updated_at)}
                          </span>
                        </div>
                        <button
                          onClick={(e) => handleDeleteChat(chat.id, e)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="w-full max-w-4xl mx-auto px-4 py-6 space-y-6">
            {loading ? (
              <div className="flex justify-center items-center h-96">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img src="/tufy.png" alt="Tufy" className="w-28 h-28 object-contain" />
                  </div>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-96 text-center px-4">
                <div className="relative mb-6 mt-20">
                  <div className="w-60 h-60 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center shadow-lg p-5">
                    <img src="/tufy.png" alt="Tufy" className="w-full h-full object-contain" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-[#1e3a5f] mb-3">
                  Hello! I'm Tufy 👋
                </h3>
                <p className="text-gray-600 max-w-md text-base leading-relaxed">
                  Your AI-powered child development assistant. I'm here to answer your questions, 
                  provide guidance, and support you on your parenting journey.
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                  <div className="bg-white/80 backdrop-blur p-4 rounded-xl border border-pink-200 shadow-sm">
                    <p className="text-sm text-gray-700">💬 Ask development questions</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur p-4 rounded-xl border border-pink-200 shadow-sm">
                    <p className="text-sm text-gray-700">🎯 Get personalized advice</p>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fadeIn`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex items-start space-x-3 max-w-[85%] md:max-w-2xl">
                      <div className="w-8 h-8 bg-pink-50 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-2 border-pink-200 p-1.5">
                        <img src="/tufy.png" alt="Tufy" className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-md border border-gray-100">
                          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                            {msg.content}
                          </p>
                        </div>
                        <div className="flex items-center mt-1.5 ml-1 space-x-2">
                          <p className="text-xs text-gray-500 font-medium">Tufy AI</p>
                          <span className="text-gray-300">•</span>
                          <p className="text-xs text-gray-400">{formatTime(msg.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {msg.role === "user" && (
                    <div className="flex items-start space-x-3 max-w-[85%] md:max-w-2xl">
                      <div className="flex-1">
                        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl rounded-tr-sm px-5 py-4 shadow-md">
                          <p className="text-sm text-white leading-relaxed">{msg.content}</p>
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5 mr-1 text-right">
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            
            {sending && (
              <div className="flex justify-start animate-fadeIn">
                <div className="flex items-start space-x-3 max-w-[85%] md:max-w-2xl">
                  <div className="w-8 h-8 bg-pink-50 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-2 border-pink-200 p-1.5">
                    <img src="/tufy.png" alt="Tufy" className="w-full h-full object-contain" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-sm px-5 py-4 shadow-md border border-gray-100">
                    <div className="flex space-x-1.5">
                      <div className="w-2.5 h-2.5 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                      <div className="w-2.5 h-2.5 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                      <div className="w-2.5 h-2.5 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 py-4 sticky bottom-0 shadow-lg">
          <div className="w-full max-w-4xl mx-auto">
            <div className="flex items-end space-x-3">
              <div className="flex-1 bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-pink-500 focus-within:ring-2 focus-within:ring-pink-200 transition-all">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message here..."
                  disabled={loading || sending}
                  rows="1"
                  className="w-full bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400 px-5 py-3.5 resize-none max-h-32"
                  style={{ minHeight: "52px" }}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={loading || sending || !message.trim()}
                className="bg-gradient-to-br from-pink-500 to-rose-600 p-3.5 rounded-2xl hover:from-pink-600 hover:to-rose-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex-shrink-0"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">Press Enter to send, Shift+Enter for new line</p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
